import React, { type CSSProperties, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NIKE_EASE } from '../../rise/constants/nikerise';

interface GradualSpacingProps {
    text: string;
    offset?: number;
    isVisible?: boolean;
    style?: CSSProperties;
    ignoreLinebreaks?: boolean;
}

export function GradualSpacing({ text = '', offset = 0, isVisible = true, style = {}, ignoreLinebreaks = false }: GradualSpacingProps) {
    const ref = React.useRef(null);

    const lines = useMemo(() => {
        const lines = ignoreLinebreaks ?
            [text.trim()] :
            text?.split('\n').map((l) => l.trim()) ?? [];

        return lines.map((line) => {
            const words = line.split(' ');

            return words.reduce((acc, word, idx) => {
                // sandwich spaces back into the sequence
                const space = idx == 0 ? undefined : ' ';
                return [...acc, space, word].filter(i => i);
            }, []);
        });
    }, [text]);

    const key = useMemo(() => {
        const randomString = (Math.random() + 1).toString(36).substring(7);
        return `${encodeURIComponent(text)}_${randomString}`;
    }, [text]);

    return (
        <AnimatePresence>
            {isVisible ? lines.map((line, idx) => (
                <div style={style}>
                    {
                        line.map((word, idx) => (
                            <motion.span
                                ref={ref}
                                key={`${key}_${idx}`}
                                initial={{
                                    opacity: 0,
                                    x: word === ' ' ? 0 : 300,
                                }}
                                animate={{
                                    opacity: 1,
                                    x: 0,
                                    transition: {
                                        duration: 1,
                                        delay: word === ' ' ? 0 : offset + idx * 0.05,
                                        ease: NIKE_EASE,
                                    },
                                }}
                                exit={{
                                    opacity: 0,
                                    x: word === ' ' ? 0 : -600,
                                    transition: {
                                        duration: 1.5,
                                        delay: word === ' ' ? 0 : offset + idx * 0.1,
                                        ease: NIKE_EASE,
                                    },
                                }}
                                style={{
                                    display: 'inline-block',
                                    ...style,
                                }}
                            >
                                {word === ' ' ? <span>&nbsp;</span> : word}
                            </motion.span>
                        ))
                    }
                </div>
            )) : null}
        </AnimatePresence>
    );
}
